import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface EditGroupModalProps {
  groupId: string
  currentName: string
  currentDescription: string | null
  onClose: () => void
  onSuccess: () => void
}

export default function EditGroupModal({
  groupId,
  currentName,
  currentDescription,
  onClose,
  onSuccess,
}: EditGroupModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [groupName, setGroupName] = useState(currentName)
  const [description, setDescription] = useState(currentDescription || '')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Update form when props change
  useEffect(() => {
    setGroupName(currentName)
    setDescription(currentDescription || '')
  }, [currentName, currentDescription])

  // Focus first input on mount
  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSubmitting])

  const updateGroup = useMutation({
    mutationFn: async (data: { name: string; description: string | null }) => {
      if (!user) throw new Error('Not authenticated')

      const { error: groupError } = await supabase
        .from('groups')
        .update({
          name: data.name.trim(),
          description: data.description?.trim() || null,
        })
        .eq('id', groupId)

      if (groupError) throw groupError
    },
    onSuccess: () => {
      // Invalidate groups queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['group', groupId] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['other-groups'] })
      queryClient.invalidateQueries({ queryKey: ['user-groups'] })
      queryClient.invalidateQueries({ queryKey: ['user-groups-sidebar'] })
      
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Basic validation
    if (!groupName.trim()) {
      setError('Group name is required')
      return
    }

    if (groupName.trim().length < 3) {
      setError('Group name must be at least 3 characters')
      return
    }

    setIsSubmitting(true)
    try {
      await updateGroup.mutateAsync({
        name: groupName.trim(),
        description: description.trim() || null,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to update group')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-group-title"
    >
      <div
        className="rounded-lg p-6 max-w-md w-full"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="edit-group-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Edit Group
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Group Name Field */}
          <div>
            <label 
              htmlFor="edit-group-name"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Group Name
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <input
              ref={nameInputRef}
              type="text"
              id="edit-group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              required
              minLength={3}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            />
          </div>

          {/* Description Field */}
          <div>
            <label 
              htmlFor="edit-group-description"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Description (Optional)
            </label>
            <textarea
              id="edit-group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your group"
              rows={4}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <div 
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSubmitting ? 'Updating...' : 'Update Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

